<template>
  <div>
    <validation-observer
      ref="personalInformationForm"
      #default="{invalid}"
    >
      <b-form
        class="auth-login-form"
        @submit.prevent="formSubmitted"
      >
        <b-row>
          <b-col
            md="12"
            class="mb-1"
          >
            <gender-selection
              :user="user"
              @updateUser="updateUser"
            />
          </b-col>
          <b-col
            v-if="false"
            md="12"
          >
            <b-form-group
              :label="$t('Interested gender')"
              label-for="lookingForGender"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Interested gender')"
                vid="lookingForGender"
                rules="required"
              >
                <div class="demo-inline-spacing">
                  <b-form-radio
                    v-model="user.lookingForGender"
                    name="lookingForGender"
                    value="male"
                  >
                    {{ $t('Male') }}
                  </b-form-radio>
                  <b-form-radio
                    v-model="user.lookingForGender"
                    name="lookingForGender"
                    value="female"
                  >
                    {{ $t('Female') }}
                  </b-form-radio>
                  <b-form-radio
                    v-model="user.lookingForGender"
                    name="lookingForGender"
                    value="both"
                  >
                    {{ $t('Both') }}
                  </b-form-radio>
                </div>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <!-- Apply a class to the form group for easier targeting -->
            <b-form-group
              label-for="userName"
              class="username-form-group"
              :label="$t('Username')"
              label-class="username-label"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Username')"
                vid="userName"
                rules="required"
              >
                <div class="position-relative"> <!-- Wrapper for positioning spinner -->
                  <b-spinner
                    v-if="userNameChecking"
                    style="position: absolute; right: 20px; top: 17%; transform: translateY(-50%); z-index: 5;"
                    small
                    class="m-1"
                    type="grow"
                  />
                  <b-form-input
                    id="userName"
                    v-model="user.userName"
                    :disabled="userNameChecking"
                    :state="errors.length > 0 ? false : (userNameAvailable || null)"
                    class="username-input"
                    :placeholder="$t('Username')"
                    @focusout="checkUsername"
                  />
                </div>
                <small class="text-danger">{{ errors[0] }}</small>
                <small
                  v-if="userNameAvailable && !errors[0]"
                  class="text-success"
                > <!-- Check for errors before showing success -->
                  {{ $t('Username is available') }}
                </small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <!-- Birthdate Section -->
          <b-col md="12">
            <!-- Container for Birthdate -->
            <b-form-group
              class="birthdate-container"
              label-for="birth-date-selectors"
              :label="$t('Birthdate')"
              label-class="birthdate-label"
            >
              <!-- Row for the selectors -->
              <b-row
                id="birth-date-selectors"
                class="birthdate-selectors-row p-0"
              >
                <b-col
                  cols="4"
                  class="birthdate-select-wrapper"
                  style="border-right: 2px solid #D9D9D9; height: 40px;"
                >
                  <!-- Removed label from b-form-group -->
                  <b-form-group label-for="date">
                    <validation-provider
                      #default="{ errors }"
                      :name="$t('Day')"
                      vid="date"
                      rules="required|notPlaceholderDay"
                    >
                      <v-select
                        id="date"
                        v-model="user.profile.date"
                        :clearable="false"
                        :options="dates"
                        :selectable="option => ! option.includes($t('Day'))"
                        class="birthdate-select"
                        :placeholder="$t('Day')"
                        :class="{'is-invalid': errors.length > 0}"
                        :searchable="false"
                        :open-direction="'top'"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col
                  cols="4"
                  class="birthdate-select-wrapper"
                  style="border-right: 2px solid #D9D9D9; height: 40px;"
                >
                  <!-- Removed label from b-form-group -->
                  <b-form-group label-for="month">
                    <validation-provider
                      #default="{ errors }"
                      :name="$t('Month')"
                      vid="month"
                      rules="required|notPlaceholderMonth"
                    >
                      <v-select
                        id="month"
                        v-model="user.profile.month"
                        :clearable="false"
                        :options="months"
                        :selectable="option => !option.text.includes($t('Month'))"
                        label="text"
                        :reduce="item => item.value"
                        class="birthdate-select"
                        :placeholder="$t('Month')"
                        :class="{'is-invalid': errors.length > 0}"
                        :searchable="false"
                        :open-direction="'top'"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col
                  cols="4"
                  class="birthdate-select-wrapper"
                >
                  <!-- Removed label from b-form-group -->
                  <b-form-group label-for="year">
                    <validation-provider
                      #default="{ errors }"
                      :name="$t('Year')"
                      vid="year"
                      rules="required|notPlaceholderYear"
                    >
                      <v-select
                        id="year"
                        v-model="user.profile.year"
                        :clearable="false"
                        :options="years"
                        :selectable="option => ! option.includes($t('Year'))"
                        class="birthdate-select"
                        :placeholder="$t('Year')"
                        :class="{'is-invalid': errors.length > 0}"
                        :searchable="false"
                        :open-direction="'top'"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-form-group>
          </b-col>
          <!-- End Birthdate Section -->

          <b-col md="12">
            <b-button
              class="mt-2 btn-primary2"
              type="submit"
              variant="primary"
              block
              :disabled="loading || invalid || !userNameAvailable"
            >
              <b-spinner
                v-if="loading"
                small
              />
              {{ $t('Continue') }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import { takeLoginRedirect } from '@/auth/utils'
import { required, email } from '@validations'
// Import extend for custom validation rules
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import {
  BRow, BCol, BButton, BForm, BFormGroup, BFormInput, BFormRadio, BSpinner,
} from 'bootstrap-vue'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import useJwt from '@/auth/jwt/useJwt'
import vSelect from 'vue-select'
import countries from '@/data/countries.json'
import { throwDefaultError } from '@/helpers/helpers'
import _ from 'lodash'
import GenderSelection from '@/components/auth/GenderSelection.vue'
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
// Removed unused import: import UsernameInput from './components/auth/UsernameInput.vue'

// --- Custom Validation Rules ---
// Usage: Add these rules to the 'rules' prop of ValidationProvider
// e.g., rules="required|notPlaceholderDay"

extend('notPlaceholderDay', {
  validate: (value, args) => value !== args.placeholder,
  message: fieldName => `${fieldName} seçimi yapmanız gerekiyor.`,
  params: ['placeholder'],
  computesRequired: true, // Makes this rule trigger 'required' logic implicitly if needed
})

extend('notPlaceholderMonth', {
  validate: (value, args) => value !== args.placeholder,
  message: fieldName => `${fieldName} seçimi yapmanız gerekiyor.`,
  params: ['placeholder'],
  computesRequired: true,
})

extend('notPlaceholderYear', {
  validate: (value, args) => value !== args.placeholder,
  message: fieldName => `${fieldName} seçimi yapmanız gerekiyor.`,
  params: ['placeholder'],
  computesRequired: true,
})
// --- End Custom Validation Rules ---

const defaultAbility = [
  {
    action: 'manage',
    subject: 'all',
  },
  {
    action: 'read',
    subject: 'all',
  },
]

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BForm,
    BFormRadio,
    BFormGroup,
    BFormInput,
    BSpinner,
    vSelect,
    GenderSelection,
    // validations
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  props: {
    password: {
      type: String,
      default: '',
      required: false,
    },
    phone: {
      type: String,
      default: '',
      required: false,
    },
  },
  data() {
    const dayPlaceholder = this.$t('Day')
    const monthPlaceholder = this.$t('Month')
    const yearPlaceholder = this.$t('Year')
    return {
      // cities data removed as it's unused
      countries: _.sortBy(countries, 'name').map(c => ({ ...c, name: this.$t(c.name) })),
      dates: [
        dayPlaceholder, '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19',
        '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '30', '31', // Ensure leading zeros for consistency if desired
      ],
      hobbies: [],
      loading: false,
      loadingHobbies: false,
      months: [
        { text: monthPlaceholder, value: monthPlaceholder }, // Placeholder object
        { text: this.$t('01'), value: '01' }, // Use string '01' etc. for consistency
        { text: this.$t('02'), value: '02' },
        { text: this.$t('03'), value: '03' },
        { text: this.$t('04'), value: '04' },
        { text: this.$t('05'), value: '05' },
        { text: this.$t('06'), value: '06' },
        { text: this.$t('07'), value: '07' },
        { text: this.$t('08'), value: '08' },
        { text: this.$t('09'), value: '09' },
        { text: this.$t('10'), value: '10' },
        { text: this.$t('11'), value: '11' },
        { text: this.$t('12'), value: '12' },
      ],
      // sideImg removed as it's unused
      user: {
        gender: 'male',
        lookingForGender: null,
        userName: '',
        phoneNumber: '',
        profile: {
          date: '26', // dayPlaceholder, // Initialize with placeholder text
          month: '08', // monthPlaceholder, // Initialize with placeholder value
          year: '2000', // yearPlaceholder, // Initialize with placeholder text
        },
        hobbies: [],
      },
      userNameAvailable: null, // Use null initially for :state binding
      userNameChanged: false,
      userNameChecking: false,
      userNameCheckingDelay: 1000,
      userNameCheckingTimeout: null,
      // userPhoneValidating and userPhoneVerificationSent removed as they seem unused
      // Validations (required, email) are imported but not explicitly used here, handled by VeeValidate rules string
      required,
      email,
      // Placeholders for validation rules
      dayPlaceholder,
      monthPlaceholder,
      yearPlaceholder,
    }
  },
  computed: {
    authType() {
      return this.$store.getters['auth/authType']
    },
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    socialRegistering() {
      return this.$store.getters['auth/socialRegistering']
    },
    tempUser(){
      return this.$store.getters['auth/tempUser']
    },
    years() {
      const _years = [this.yearPlaceholder] // Use placeholder constant
      const currentYear = new Date().getFullYear()
      const lastYear = currentYear - 18 // Minimum age 18
      const firstYear = currentYear - 100 // Max age approx 100

      for (let i = lastYear; i >= firstYear; i--) {
        _years.push(i.toString())
      }

      return _years
    },
  },
  watch: {
    'user.userName' (val) {
      this.userNameAvailable = null; // Reset availability state on change
      if (val && val.trim() !== '') { // Check for non-empty, non-whitespace string
        clearTimeout(this.userNameCheckingTimeout)
        this.userNameCheckingTimeout = setTimeout(() => {
          this.checkUsername()
        }, this.userNameCheckingDelay)
      } else {
        this.userNameAvailable = false // Explicitly false if empty/whitespace
        clearTimeout(this.userNameCheckingTimeout) // Clear timeout if input becomes empty
        this.userNameChecking = false // Ensure checking stops
      }
    },
    // Watch date parts to potentially re-validate date combinations if needed
    'user.profile.date': function() { this.validateBirthdate() },
    'user.profile.month': function() { this.validateBirthdate() },
    'user.profile.year': function() { this.validateBirthdate() },
  },
  created() {
    // this.getHobbies() // Commented out in original
    this.$emit('updateUser', null)
    // Initial values are now set directly in data() using placeholders

    this.generateUsername()
  },
  methods: {
    // Add a method to re-trigger validation if complex date logic is needed (e.g., checking days in month)
    validateBirthdate() {
      // Example: If you need to validate the combination of day/month/year
      // this.$refs.personalInformationForm.validate(); // This validates the whole form
      // Or trigger specific field validation if possible with VeeValidate API
    },
    checkUsername() {
      if (this.user.userName && this.user.userName.trim() !== '') {
        this.userNameChecking = true
        this.userNameAvailable = null // Indicate checking in progress
        // Ensure ValidationProvider doesn't show old errors during check
        if (this.$refs.personalInformationForm) {
          const userNameField = this.$refs.personalInformationForm.fields.userName
          if (userNameField) {
            // userNameField.untouched(); // Reset validation state/errors for this field
          }
        }
        useJwt.checkUsername({ username: this.user.userName, lang: this.$i18n.locale })
          .then(() => {
            this.userNameAvailable = true
          })
          .catch(error => {
            this.userNameAvailable = false
            // Use VeeValidate to set the error message based on API response or generic message
            const errorMessage = error.response?.data?.message || this.$t('You cannot use this username')
            if (this.$refs.personalInformationForm) {
              this.$refs.personalInformationForm.setErrors({ userName: [errorMessage] })
            }
          })
          .finally(() => {
            this.userNameChecking = false
          })
      } else {
        this.userNameAvailable = false // Invalid if empty or whitespace only
        this.userNameChecking = false
        if (this.$refs.personalInformationForm) { // Clear potential previous errors if field is cleared
          const userNameField = this.$refs.personalInformationForm.fields.userName
          if (userNameField) {
            // userNameField.untouched();
          }
        }
      }
    },
    countrySearch(options, search) {
      // Unused method
      return options.filter(country => {
        return country.name.toLowerCase().includes(search.toLowerCase())
            || country.code.toLowerCase().includes(search.toLowerCase())
      })
    },
    formSubmitted() {
      this.$refs.personalInformationForm.validate().then(success => {
        // Ensure username check isn't pending and is successful
        const isUsernameValid = this.userNameAvailable === true && !this.userNameChecking

        if (success && isUsernameValid) {
          if (this.socialRegistering) {
            // Profil bilgilerini güncelle
            const data = {
              userName: this.user.userName,
              deviceId: this.$store.getters['app/deviceId'],
            }
            const profile = {
              gender: this.user.gender,
              // Send actual value, not placeholder
              date: this.user.profile.date === this.dayPlaceholder ? null : this.user.profile.date,
              month: this.user.profile.month === this.monthPlaceholder ? null : this.user.profile.month,
              year: this.user.profile.year === this.yearPlaceholder ? null : this.user.profile.year,
            }
            // Looking for gender seems unused (v-if="false"), setting default or removing
            const searchCriteria = {
              gender: this.user.lookingForGender || 'both', // Default or handle if needed
            }

            this.loading = true

            const token = this.tempUser.loginToken.token

            const _updateMe = useJwt.updateMe(data, token)
            const _updateProfile = useJwt.updateProfile(profile, token)
            const _updateSearchCriteria= useJwt.updateSearchCriteria(searchCriteria, token)
            // const _updateMyInterests = useJwt.updateMyInterests(this.user.hobbies.map(i => i.code), token)

            Promise.all([_updateMe, _updateProfile, _updateSearchCriteria])
              .then(() => {
                // useJwt.updateMyInterests(this.user.hobbies.map(i => i.code), token)
                useJwt.getMe(token)
                  .then(response => {
                    const user = response.data.body
                    this.$store.dispatch('auth/setGoogleUser', null)
                    this.$store.dispatch('auth/setSocialRegistering', false)
                    const defaultAbility = [
                      {
                        action: 'manage',
                        subject: 'all',
                      },
                      {
                        action: 'read',
                        subject: 'all',
                      },
                    ]
                    useJwt.setToken(token)
                    this.$store.dispatch('auth/setToken', token)
                    useJwt.claimStoredReferral()
                    this.$store.dispatch('auth/updateUser', {
                      ability: defaultAbility,
                      ...user,
                    })
                    this.$ability.update(defaultAbility)
                    this.$router.push('/')
                  })
                  .catch(error => {
                    throwDefaultError(this.$toast, error)
                  })
                  .finally(() => { this.loading = false })
              })
              .catch(error => {
                throwDefaultError(this.$toast, error)
                this.loading = false // Ensure loading is false on error
              })
            // Removed duplicate finally block
          } else {
            // Update temp user with actual values, checking for placeholders
            const userDataToStore = {
              ...this.user,
              profile: {
                date: this.user.profile.date === this.dayPlaceholder ? null : this.user.profile.date,
                month: this.user.profile.month === this.monthPlaceholder ? null : this.user.profile.month,
                year: this.user.profile.year === this.yearPlaceholder ? null : this.user.profile.year,
              },
            }

            // this.$store.dispatch('auth/updateTempUser2', userDataToStore)
            // this.$emit('updateActiveComponent', 'Password')
            // this.$emit('updateBackButton', 'PersonalInformation')

            if (this.authType === 'email') {
              const profile = {
                gender: userDataToStore.gender,
                country: userDataToStore.country,
                lookingForGender: userDataToStore.lookingForGender,
                userName: userDataToStore.userName,
                date: userDataToStore.profile.date,
                month: userDataToStore.profile.month,
                year: userDataToStore.profile.year,
              }

              this.loading = true

              useJwt.putPendingUser(profile)
                .then(() => {
                  useJwt.claimStoredReferral()
                  this.$ability.update(defaultAbility)
                  this.$router.push(takeLoginRedirect() || '/')
                })
                .catch(error => {
                  throwDefaultError(this.$toast, error)
                  this.loading = false
                })
            } else {
              const userData = {
                /// ...this.user,
                country: userDataToStore.country,
                gender: userDataToStore.gender,
                lookingForGender: userDataToStore.lookingForGender,
                userName: userDataToStore.userName,
                phoneNumber: this.phone,
                password: this.password,
                date: userDataToStore.profile.date,
                month: userDataToStore.profile.month.value,
                year: userDataToStore.profile.year,
                lang: this.$i18n.locale,
                deviceId: this.$store.getters['app/deviceId'],
              }
              useJwt.register(userData)
                .then(response => {
                  gtag('event', 'conversion', {
                    send_to: 'AW-805828651/lOrECPO8oYIBEMPwn4AD',
                    user: response.data.body,
                  })

                  useJwt.setToken(response.data.body.loginToken.token)
                  this.$store.dispatch('auth/setToken', response.data.body.loginToken.token)
                  useJwt.claimStoredReferral()
                  this.$store.dispatch('auth/updateUser', {
                    ability: defaultAbility,
                    ...response.data.body,
                  })
                  this.$ability.update(defaultAbility)
                  this.$router.push(takeLoginRedirect() || '/')
                })
                .catch(() => {
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: this.$t('You should check your login information and try again'),
                      variant: 'danger',
                    },
                  })
                })
                .finally(() => {
                  this.loading = false
                })
            }
          }
        } else {
          // Handle case where form is invalid or username not available/checked
          if (!isUsernameValid && this.user.userName !== '' && !this.userNameChecking) {
            // If username is filled but not available/checked (and not currently checking), re-trigger check
            this.checkUsername()
          }
          // Optionally provide user feedback about what's missing
          if (!success) {
            // Find first invalid field and focus it (optional)
            const firstError = Object.keys(this.$refs.personalInformationForm.errors).find(
              field => this.$refs.personalInformationForm.errors[field].length > 0,
            )
            if (firstError) {
              const fieldElement = document.getElementById(firstError) // Assuming IDs match vids
              if (fieldElement) fieldElement.focus()
            }
          } else if (!isUsernameValid) {
            const userNameInput = document.getElementById('userName')
            if (userNameInput) userNameInput.focus()
          }
        }
      })
    },
    generateUsername() {
      useJwt.generateUsername()
        .then(response => {
          console.log('generateUsername response', response)
          this.user.userName = response.data.body
        })
    },
    getHobbies() {
      // Commented out in original
      this.loadingHobbies = true
      setTimeout(() => {
        this.hobbies = [
          {
            code: 1,
            name: 'Spor',
          },
          {
            code: 2,
            name: 'Müzik',
          },
          {
            code: 3,
            name: 'Dans',
          },
        ]
        this.loadingHobbies = false
      }, 500)
      // TODO: backenddeki hata giderildikten sonra açılacak
      // useJwt.getInterests()
      //     .then(response => {
      //       this.hobbies = response.data.body
      //     })
      //     .catch(error => {
      //       this.$toast({
      //         component: ToastificationContent,
      //         props: {
      //           title: 'Hobiler yüklenirken beklenmedik bir hata oldu',
      //           variant: 'danger',
      //         },
      //       })
      //     })
      //     .finally(() => this.loadingHobbies = false)
    },
    updateUser(updatedUser) {
      this.user = { ...this.user, ...updatedUser }
    },
  },
}
</script>

<style lang="scss"> /* Keep lang="scss" */
// Import base styles if not already globally imported
@import '@core/scss/vue/pages/page-auth.scss';
@import '@core/scss/vue/libs/vue-wizard.scss';
@import '@core/scss/vue/libs/vue-select.scss';

// Helper function or variable for primary color
$primary-color: #eb81d2; // Example primary color
$border-color: #d8d6de; // Default border color
$text-color: #6e6b7b; // Default text color
$error-color: #ea5455; // Error color

// Custom styles for the username input to match the image
.username-form-group {
  position: relative;
  padding-top: 10px; // Make space for the absolutely positioned label
  margin-bottom: 1rem; // Ensure spacing below the input group

  // Style the label to look like the small floating box
  .username-label {
    //position: absolute;
    //top: 12px; // Position at the top of the padded form-group area
    //left: 20px; // Indent from the left
    //transform: translateY(-50%); // Center vertically on border
    //padding: 4px 10px; // Adjust padding (top/bottom, left/right)
    font-size: 0.75rem; // Small font size (e.g., 12px)
    //color: $text-color;
    //background-color: #fdf8fd; // White background to overlay the input border
    //border: 1px solid $border-color;
    border-radius: 10px; // Rounded corners for the label box
    //line-height: 1.4; // Adjust line-height for vertical centering
    z-index: 2; // Ensure label is above the input field's border and container border
    display: inline-block; // Ensure background doesn't take full width
    max-width: calc(100% - 40px); // Prevent label from overflowing
    white-space: nowrap; // Prevent wrapping
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // Style the input field itself
  .username-input.form-control {
    display: block;
    width: 100%;
    padding: 1.8rem 1rem; // Adjust padding (e.g., 16px top/bottom/left/right)
    // Add more right padding if spinner is present
    padding-right: 3rem; // Make space for the spinner on the right
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: $text-color; // Input text color
    background-color: #fdf8fd;
    background-clip: padding-box;
    border: 1px solid $border-color; // Default border color
    appearance: none;
    border-radius: 8px; // High border-radius for the outer container look
    transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;

    &::placeholder {
      color: #b9b9c3; // Placeholder color
      opacity: 1;
    }

    // Adjust border color on focus to match theme primary
    &:focus {
      border-color: $primary-color; // Primary color
      outline: 0;
      // Optional: add a subtle shadow matching Bootstrap's focus
      // box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
    }

    // State-specific border colors (ensure they don't override focus style undesirably)
    // Using Bootstrap's default classes is-invalid/is-valid
    &.is-invalid {
      border-color: $error-color !important; // Danger color (use !important if needed)
      // Add padding if Bootstrap adds an icon, otherwise spinner padding is enough
    }

    &.is-valid {
      border-color: #28c76f !important; // Success color (use !important if needed)
      // Add padding if Bootstrap adds an icon, otherwise spinner padding is enough
    }
  }

  // Ensure spinner inside the relative div is positioned correctly
  .position-relative .b-spinner {
    right: 20px; // Position from the right edge of the input padding area
    top: 50%;
    transform: translateY(-50%);
    z-index: 5; // Keep spinner above input background
  }

  // Adjust positioning for RTL layouts if necessary
  [dir="rtl"] & {
    .username-label {
      left: auto;
      right: 20px;
    }
    .username-input.form-control {
      padding-left: 3rem; // Spinner space on the left
      padding-right: 1rem;
    }
    .position-relative .b-spinner {
      right: auto;
      left: 20px;
    }
  }
}

// --- Styles for Birthdate Picker ---
.birthdate-container {
  position: relative;
  padding-top: 4px; // Space for label + internal padding
  padding-bottom: 4px; // Spacing below selectors
  border: 1px solid $border-color;
  border-radius: 8px; // Rounded container
  margin-top: 1.5rem; // Space above container (adjust as needed)
  margin-bottom: 1rem; // Default spacing
  background-color: #fdf8fd;
  height: 52px;

  // Style the main "Birthdate" label
  .birthdate-label {
    position: absolute;
    top: -28px;
    left: 0;
    //transform: translateY(-50%); // Center vertically on the border
    //padding: 2px 10px;
    font-size: 0.75rem;
    //color: $text-color;
    //background-color: #fdf8fd; // Must match page background
    //border: 1px solid $border-color;
    //border-radius: 10px;
    //line-height: 1.4;
    z-index: 2; // Above container border but below dropdown menu
    display: inline-block;
    white-space: nowrap;
  }
}

.birthdate-selectors-row {
  padding-left: 0.5rem; // Inner spacing for row
  padding-right: 0.5rem;
  padding-top: 0.5rem; // Space below the floating label inside the container
  display: flex; // Ensure columns behave well
  align-items: flex-start; // Align items top for consistent error message placement
}

.birthdate-select-wrapper {
  text-align: center; // Center the content (select) within the column
  padding-left: 0.5rem; // Spacing between columns
  padding-right: 0.5rem;

  // Hide the default <label> for Day, Month, Year inside the container
  // We use the main floating label instead.
  .form-group > label:not(.birthdate-label) { // Target only the inner labels
    display: none;
  }

  // Adjust margin for form-group inside columns if needed
  .form-group {
    margin-bottom: 0; // Remove default bottom margin if causing spacing issues
  }
}

.birthdate-select {
  // Override v-select default appearance
  .vs__dropdown-toggle {
    border: none !important;
    background: transparent !important;
    padding: 0;
    height: auto;
    box-shadow: none !important;
    width: 100%; // Ensure it fills the wrapper
  }

  .vs__selected-options {
    padding: 0;
    margin: 0;
    flex-wrap: nowrap; // Prevent wrapping
    justify-content: center; // Center the selected item horizontally
    width: 100%;
    min-height: 40px; // Ensure minimum height for lines and text
    align-items: center; // Vertically center text
    position: relative; // For pseudo-elements

    // Purple lines - applied here to cover placeholder state too
    &::before,
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      width: 80%; // Make lines wider
      height: 2px; // Thickness
      // background-color: #fff;
      z-index: 1; // Below text
    }

    &::before {
      top: 2px; // Position top line slightly offset from edge
    }

    &::after {
      bottom: 2px; // Position bottom line slightly offset from edge
    }
  }

  // Style the selected value text
  .vs__selected {
    font-size: 1.5rem !important; // Large font
    font-weight: bold !important;
    color: #878787 !important; // Black
    margin: 0 !important;
    padding: 0.5rem 0 !important; // Vertical padding
    line-height: 1.2 !important;
    text-align: center !important;
    z-index: 2;
    width: auto; // Let text determine width within flex container
    background-color: transparent; // Ensure no background interferes
    position: absolute;
  }

  // Style the search input (which often holds the placeholder)
  .vs__search {
    font-size: 1.5rem !important; // Match selected style
    font-weight: bold !important;
    color: $text-color !important; // Use text color for placeholder
    margin: 0 !important;
    padding: 0.5rem 0 !important;
    line-height: 1.2 !important;
    text-align: center !important;
    position: relative;
    z-index: 2;
    width: 100%; // Full width to center placeholder text correctly

    &::placeholder {
      color: $text-color !important; // Style placeholder explicitly
      opacity: 1;
    }
  }

  // Hide dropdown arrow and clear button
  .vs__actions {
    display: none !important;
  }

  // Style the dropdown menu
  .vs__dropdown-menu {
    min-width: auto;
    width: 100%; // Match width of the toggle
    max-height: 200px; // Limit height
    overflow-y: auto;
    text-align: center;
    border: 1px solid $border-color;
    box-shadow: none;
    margin-top: 5px; // Small gap between selector and dropdown
    z-index: 9999 !important; // Ensure dropdown is on top
  }

  .vs__dropdown-option {
    padding: 0.5rem 1rem;
    text-align: center; // Center options
    font-size: 1rem; // Standard font size for options
    font-weight: normal;
    color: $text-color; // Default option color
  }

  .vs__dropdown-option--highlight {
    background-color: $primary-color;
    color: #fff !important; // Ensure text is white on highlight
  }
  // Style when invalid
  &.is-invalid {
    .vs__selected-options::before,
    .vs__selected-options::after {
      background-color: $error-color; // Change line color to red
    }
    // Optionally change text color too, but lines are usually enough indication
    // .vs__selected { color: $error-color !important; }
  }
}

// Small text for errors below the selectors
.birthdate-select-wrapper .text-danger {
  display: block;
  text-align: center;
  margin-top: 0.25rem;
  font-size: 0.75rem; // Smaller error text
  min-height: 1em; // Reserve space even if no error
}

// Ensure vue-select dropdown appends to body correctly if needed
.v-select-body-appended {
  z-index: 9999 !important; // Make sure dropdown is above everything else
}

// RTL adjustments if needed
[dir="rtl"] {
  .birthdate-container .birthdate-label {
    left: auto;
    right: 20px;
  }
}
.birthdate-select .vs__dropdown-menu {
  top: auto !important;
  bottom: calc(100% + 4px) !important;
  border-radius: 4px 4px 0 0 !important;
}

.birthdate-select.vs--open .vs__open-indicator {
  transform: rotate(180deg) !important;
}
</style>
